import { DATASET } from "./datasets/Constants";
import DataSetFactory from "./datasets/DataSetFactory";
import { registerSchema, subscribeSchema } from "./schemas/ConfigSchema";
import { setConfig } from "./types/Config";
import { inputValidator } from "./utils/validator";
export const subscribers = new Map();
const pendingUnsubscriptions = new Map();
//import order is important due to circular dependencies...project needs refactoring
import { isSocketClosed, notif } from "./RTDNotifV2";
export class LegacySubscriptionHandler {
    constructor() {
        this.DATASET = DATASET;
    }
    /**
     * This function registers the application and create a new web socket connection
     * @function init
     * @param { Register } config
     */
    /**
     * @typedef {Object} Register
     * @property {string} accessToken - Authorization Token
     * @property {string} notifHost - Domain of notifs
     * @property {string} rtdHost - Domain of rtd
     * @property {string=} rtdUrl - Full URL of rtd service
     * @property {string=} notifUrl - Full URL of notif-data
     * @property {string} clientType - application from where it is initialized
     * @property {string=} lostConnectionRecoveryTimeout - time limit in milliseconds to retry socket connection, default value is 2min
     * @property {boolean=} notifsWelcomeFlag
     * @property {number=} welcomeTimeout
     * @property {string} pingURL - URL that ping for connectivity status
     * @property {string} pingEvent - custom event name dispatched for connectivity status
     */
    async init(config) {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
            if (this.dataSetFactory) {
                reject("[RTDWC] RTDWC already initialized");
            }
            const validator = inputValidator(registerSchema, config);
            if (!validator.success) {
                this.error = true;
                reject(`[RTDWC] incorrect payload, ${validator.errorMessage}`);
            }
            // initializing optional fields
            config.lostConnectionRecoveryTimeout = config.lostConnectionRecoveryTimeout || "120000";
            config.welcomeTimeout = config.welcomeTimeout || 7000;
            config.notifsWelcomeFlag = config.notifsWelcomeFlag || false;
            setConfig(config);
            try {
                // to uncomment
                this.dataSetFactory = await new DataSetFactory();
                this.error = false;
                resolve("[RTDWC] dataSetFactory initialized");
            }
            catch (error) {
                this.error = true;
                reject(error);
            }
        });
    }
    async callSubscriptionApi(datasetName) {
        const pendingUnsubscription = pendingUnsubscriptions.get(datasetName);
        const subscriberDataSet = subscribers.get(datasetName);
        if (pendingUnsubscription) {
            pendingUnsubscription
                .then(async () => {
                await notif.subscribe(datasetName, subscriberDataSet);
                pendingUnsubscriptions.delete(datasetName);
            })
                .catch(async (err) => {
                // eslint-disable-next-line no-console
                console.error(`[RTDWC] Error while unsubscription ${err} | timestamp=${new Date()}, UTC=${new Date().toUTCString()}`);
                await notif.subscribe(datasetName, subscriberDataSet);
                pendingUnsubscriptions.delete(datasetName);
            });
        }
        else {
            await notif.subscribe(datasetName, subscriberDataSet);
        }
    }
    /**
     * This function is to subscribe dataset
     * @function subscribe
     * @param { Subscribe } dataset
     */
    /**
     * @typedef {Object} Subscribe - creates a new type named 'Subscribe'
     * @property {string} datasetName - name of dataset
     * @property {Function} update - callback function to provide success data
     * @property {Function} error - callback function to provide error data
     */
    async subscribe(dataset) {
        let unsubscribed = false; // to prevent same widget from unsubscribing multiple times
        if (this.error || !this.dataSetFactory || isSocketClosed) {
            throw new Error("[RTDWC] Websocket initialization not successful");
        }
        const validator = inputValidator(subscribeSchema, dataset);
        if (!validator.success) {
            throw new Error(`[RTDWC] incorrect payload, ${validator.errorMessage}`);
        }
        if (!dataset.error) {
            dataset.error = function () {
                return;
            };
        }
        const { datasetName, update, error, connectionStatus } = dataset;
        const subscriberDataSet = subscribers.get(datasetName);
        //Calling the connectionStatus function if widget is calling subscribe but no websocket connection present
        if (isSocketClosed) {
            // eslint-disable-next-line sonarjs/no-gratuitous-expressions
            connectionStatus?.(!isSocketClosed);
        }
        if (subscriberDataSet) {
            if (subscriberDataSet.data) {
                subscriberDataSet.data.datasetValues = [...subscriberDataSet.data.datasetValues];
                update(subscriberDataSet.data);
            }
            else if (subscriberDataSet.isError && !subscriberDataSet.isInProgress) {
                await this.callSubscriptionApi(datasetName);
            }
        }
        else {
            subscribers.set(datasetName, {
                data: null,
                callbacks: [
                    {
                        update,
                        error,
                        connectionStatus
                    }
                ],
                isError: false,
                isInProgress: true
            });
            const dataSet = this.dataSetFactory.createDataSet(datasetName);
            dataSet.subscribe((data, delta) => {
                const subs = subscribers.get(datasetName);
                if (subs) {
                    subs.data = data;
                    subs.callbacks.forEach((sub) => {
                        data.datasetValues = [...data.datasetValues];
                        if (delta) {
                            delta.datasetValues = [...delta.datasetValues];
                        }
                        sub.update(data, delta);
                    });
                }
            }, (err) => {
                const subs = subscribers.get(datasetName);
                if (subs) {
                    subs.callbacks.forEach((sub) => sub.error(err));
                }
            }, (isConnected) => {
                const subs = subscribers.get(datasetName);
                if (subs) {
                    subs.callbacks.forEach((sub) => sub.connectionStatus?.(isConnected));
                }
            });
            // calling subscribe api if dataset is not present in rtdwc
            await this.callSubscriptionApi(datasetName);
        }
        return {
            unsubscribe() {
                if (unsubscribed) {
                    throw new Error("[RTDWC] Dataset already unsubscribed");
                }
                const dataValue = subscribers.get(datasetName);
                if (dataValue) {
                    if (dataValue?.callbacks?.length) {
                        dataValue.callbacks = dataValue.callbacks.filter((cb) => cb.update !== dataset.update) || [];
                    }
                    if (dataValue.callbacks?.length === 0) {
                        // unsubscribe to push data when there is not a single subscription from any widget
                        subscribers.delete(datasetName);
                        pendingUnsubscriptions.set(datasetName, notif.unsubscribe(datasetName));
                    }
                    unsubscribed = true;
                }
                else {
                    throw new Error("[RTDWC] Dataset not subscribed");
                }
            }
        };
    }
}
